import React, {useEffect, useState} from "react";
import {Page} from "@shopify/polaris";
import RecommendationStats from "../../components/RecommendationStats/RecommendationStats";
import {useBilling} from "../../hooks/useBilling";
import {useRecommendations} from "../../hooks/useRecommendations";
import {useProductSync} from "../../hooks/useProductSync";
import BillingLoader from "../../components/BillingLoader/BillingLoader";
import SyncProgressBanner from "../../components/SyncProgressBanner/SyncProgressBanner";
import RecommendationTable from "../../components/RecommendationTable/RecommendationTable";
import {BulkRecommendationModal} from "../../components/BulkRecommendationModal/BulkRecommendationModal";
import {RecommendationModal} from "../../components/RecommonedationModal/RecommendationModal";
import {deleteRecommendationAPI, toggleRecommendationAPI} from "../../api/axiosRequests";

export interface ProductImage {
    src: string;
    alt?: string;
}

export interface Product {
    id: number;
    shopify_product_id: number;
    title: string;
    price?: string;
    images?: ProductImage[];
}

export interface Recommendation {
    id: number;
    product_id: number;
    recommended_product_id: number;
    algo: string;
    enabled: boolean | number;
    impressions: number;
    clicks: number;
    conversions: number;
    conversion_rate: number;
}

export default function RecommendationsPage() {
    const shop = new URLSearchParams(window.location.search).get("shop");
    const searchParams = new URLSearchParams(window.location.search);
    const host = searchParams.get("host") || "";

    const [bulkModalOpen, setBulkModalOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<"create" | "edit">("create");
    const [editingRec, setEditingRec] = useState<Recommendation | undefined>(undefined);

    const {
        checkingBilling,
        billingActive,
        showToast,
        checkBilling,
        isFreePlan,
        createBilling,
        handleCancelSubscription,
    } = useBilling(host, shop);

    const {
        recommendations,
        productMap,
        searchValue,
        setSearchValue,
        page,
        setPage,
        totalPages,
        loading,
        fetchRecs,
    } = useRecommendations(shop, showToast, billingActive);

    const { syncing, syncProgress, syncMessage, handleSyncProducts } = useProductSync(
        shop,
        showToast,
        fetchRecs
    );

    useEffect(() => {
        if (shop) {
            (async () => await checkBilling())();
        }
        // eslint-disable-next-line
    }, [shop]);

    // EDIT modal Handlers
    const handleEdit = (rec: Recommendation) => {
        setModalMode("edit");
        setEditingRec(rec);
        setModalOpen(true);
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Delete this recommendation?')) return;
        try {
            await deleteRecommendationAPI(id);
            showToast('Deleted');
            await fetchRecs();
        } catch {
            showToast('Failed to delete');
        }
    };

    const handleToggle = async (id: number, enabled: boolean) => {
        const activated = enabled;
        try {
            await toggleRecommendationAPI(id, shop, enabled);
            showToast((activated ? 'Activated' : 'Deactivated') + ' Successfully');
            await fetchRecs();
        } catch {
            showToast((activated ? 'Activation' : 'Deactivated') + ' Failed');
        }
    };

    if (checkingBilling) return <BillingLoader message="Checking billing status..." />;
    // if (!billingActive) return <BillingLoader message="Redirecting for billing..." />;

    return (
        <Page
            fullWidth
            title="Upsell Recommendations"
            primaryAction={{
                content: syncing ? "Syncing Products..." : "Sync Products",
                onAction: handleSyncProducts,
                disabled: syncing,
            }}
            secondaryActions={[
                {
                    content: "+ Add Recommendation",
                    onAction: () => {
                        setBulkModalOpen(true);
                    },
                },
                {
                    content: !billingActive ? "Upgrade Subscription" : "Cancel Subscription",
                    onAction: !billingActive ? createBilling : handleCancelSubscription
                }
            ]}
        >
            {syncing && <SyncProgressBanner progress={syncProgress} message={syncMessage} />}
            <RecommendationStats shop={shop} showToast={showToast} isBillingActive={billingActive}/>
            <RecommendationTable
                shop={shop}
                loading={loading}
                recommendations={recommendations}
                productMap={productMap}
                page={page}
                setPage={setPage}
                totalPages={totalPages}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                onDelete={handleDelete}
                onEdit={handleEdit}
                onToggle={handleToggle}
                isFreePlan={isFreePlan}
                isBillingActive={billingActive}
            />

            <BulkRecommendationModal
                open={bulkModalOpen}
                shop={shop}
                existingPairs={recommendations.map((r: Recommendation) => ({
                    product_id: r.product_id,
                    recommended_product_id: r.recommended_product_id,
                }))}
                onClose={() => setBulkModalOpen(false)}
                onSaved={() => {
                    showToast("Recommendations saved");
                    (async () => await fetchRecs())();
                    setBulkModalOpen(false);
                }}
            />

            <RecommendationModal
                open={modalOpen}
                mode={modalMode}
                editData={
                    editingRec && {
                        id: editingRec.id,
                        product_id: editingRec.product_id,
                        recommended_product_id: editingRec.recommended_product_id,
                    }
                }
                shop={shop}
                productMap={productMap}
                existingPairs={recommendations.map((r: Recommendation) => ({
                    product_id: r.product_id,
                    recommended_product_id: r.recommended_product_id,
                }))}
                onClose={() => setModalOpen(false)}
                onSaved={() => {
                    showToast("Saved");
                    (async () => await fetchRecs())();
                    setModalOpen(false);
                }}
            />

        </Page>
    );
}
